export const EDITOR_SETTINGS_KEY = "progsity:playground:editor-settings";

export type EditorSettings = {
  fontSize: number;
  consoleFontSize: number;
  theme: "dark" | "light";
  tabSize: 2 | 4 | 8;
  wordWrap: boolean;
  minimap: boolean;
};

export const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  fontSize: 14,
  consoleFontSize: 14,
  theme: "dark",
  tabSize: 4,
  wordWrap: false,
  minimap: false,
};

export const FONT_SIZE_MIN = 12;
export const FONT_SIZE_MAX = 54;
export const CONSOLE_FONT_SIZE_MIN = 12;
export const CONSOLE_FONT_SIZE_MAX = 64;

/** Editor font size bounds — zoom and settings panel use the same range. */
export function clampFontSize(n: number): number {
  if (Number.isNaN(n)) return DEFAULT_EDITOR_SETTINGS.fontSize;
  return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(n)));
}

export function bumpFontSize(current: number, delta: number): number {
  return clampFontSize(current + delta);
}

export function clampConsoleFontSize(n: number): number {
  if (Number.isNaN(n)) return DEFAULT_EDITOR_SETTINGS.consoleFontSize;
  return Math.min(
    CONSOLE_FONT_SIZE_MAX,
    Math.max(CONSOLE_FONT_SIZE_MIN, Math.round(n)),
  );
}

function normalizeTabSize(n: number): 2 | 4 | 8 {
  if (n === 2 || n === 8) return n;
  return 4;
}

export function loadEditorSettings(): EditorSettings {
  if (typeof window === "undefined") return DEFAULT_EDITOR_SETTINGS;
  try {
    const raw = window.localStorage.getItem(EDITOR_SETTINGS_KEY);
    if (!raw) return DEFAULT_EDITOR_SETTINGS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const fontSize = clampFontSize(Number(parsed["fontSize"]));
    const consoleFontSize = clampConsoleFontSize(Number(parsed["consoleFontSize"]));
    const tabSize = normalizeTabSize(Number(parsed["tabSize"]));
    const theme = parsed["theme"] === "light" ? "light" : "dark";
    const wordWrap = Boolean(parsed["wordWrap"]);
    const minimap = Boolean(parsed["minimap"]);
    return { fontSize, consoleFontSize, tabSize, theme, wordWrap, minimap };
  } catch {
    return DEFAULT_EDITOR_SETTINGS;
  }
}

export function persistEditorSettings(s: EditorSettings): void {
  try {
    window.localStorage.setItem(EDITOR_SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* quota / private mode */
  }
}
